import React from "react";
import { CreditCard, Loader2 } from "lucide-react";
import { useTheme } from "../hooks/useTheme";

/**
 * Coach plan cards — mirrors the client MembershipPlans card look so the coach
 * payment UI matches. Shared by the coach signup step and the coach
 * /coaching/membership/plans page so there's one place to maintain.
 *
 * `plans` come from the coach plans endpoint. Each carries a `Features` list of
 * the premium feature display names it unlocks and an `IsMostPopular` flag (set
 * by an admin in the tier editor). `onChoose(plan)` runs the subscribe/select.
 * `trialAvailable`: the coach would get a plan's free trial (a first subscription
 * only). `actionFor(plan)` optionally gives a card's button {label, disabled}
 * (Upgrade / Downgrade / Switch, or a plan already picked for the renewal).
 */

// Feature checklist mirrors the client cards: base coach tools every coach
// already has (always shown ticked) + premium features that need a membership
// (ticked only when this tier unlocks them, otherwise greyed out).
//old const COACH_BASE_FEATURES = ["Coaching dashboard", "Manage your students", "Track prospects"];
const COACH_BASE_FEATURES = ["Coaching dashboard", "Manage your clients", "Track prospects"];
const COACH_PREMIUM_FEATURES = ["Coach Reporting", "My Program"];

// monthly -> /mo, yearly -> /yr, one-time -> ' one-time'
function billingSuffix(freq) {
    if (!freq || freq === "monthly") return "/mo";
    if (freq === "yearly") return "/yr";
    if (freq === "one-time") return " one-time";
    return `/${freq}`;
}

//old export default function CoachPlanCards({ plans, onChoose, busyTierId, currentTierId, promo }) {
export default function CoachPlanCards({ plans, onChoose, busyTierId, currentTierId, promo, trialAvailable = true, actionFor }) {
    const { isDarkMode } = useTheme();

    if (!plans || plans.length === 0) {
        return <p className={`text-center py-12 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>No coaching plans are available right now.</p>;
    }

    // Keep cards a consistent client-like size: with only 1–2 coach tiers a full
    // 3-col grid stretches them too wide (and leaves an empty column), so the
    // layout adapts to the count and stays centered.
    const n = plans.length;
    const isSingle = n === 1;
    // If any plan shows a trial line, reserve that line on every card so the
    // rows stay aligned; if none do, no card reserves it (no wasted space).
    //old const anyTrial = plans.some((p) => p.TrialDays);
    // (a plan's trial only for a coach who gets it, and not on the plan they're on)
    const anyTrial = trialAvailable && plans.some((p) => p.TrialDays && p.TierID !== currentTierId);
    const containerCls =
        n === 1 ? "flex justify-center"
        : n === 2 ? "grid grid-cols-1 sm:grid-cols-2 gap-6 items-stretch max-w-2xl mx-auto"
        : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch";

    return (
        <div className={containerCls}>
            {plans.map((plan) => {
                const isCurrent = currentTierId && plan.TierID === currentTierId;
                // Non-null only when an applied promo code covers this plan.
                const promoTier = promo?.tiers?.find((t) => t.TierID === plan.TierID) || null;
                //const promoFree = promoTier && promo.discountType === "free";
                const promoFree = promoTier && promo.discountType === "free" && Number(plan.Price) > 0;
                const isFeatured = plan.IsMostPopular === true || plan.IsMostPopular === 1;
                const busy = busyTierId === plan.TierID;
                const action = !isCurrent && actionFor ? actionFor(plan) : null;
                const unlocked = plan.Features || [];

                const features = [
                    ...COACH_BASE_FEATURES.map((label) => ({ label, included: true })),
                    ...COACH_PREMIUM_FEATURES.map((label) => ({ label, included: unlocked.includes(label) })),
                ];

                const outerBorder = promoTier
                    ? `border-primary-500 shadow-xl ${isDarkMode ? "bg-slate-800" : "bg-white"}`
                    : isCurrent
                    ? `border-primary-400 shadow-[0_0_0_3px_rgba(52,211,153,0.25)] ${isDarkMode ? "bg-slate-800" : "bg-white"}`
                    : isFeatured
                        ? `border-primary-500 shadow-xl ${isDarkMode ? "bg-slate-800" : "bg-white"}`
                        : `shadow-sm ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"}`;

                const btnCls = isCurrent
                    ? `border-primary-400 cursor-default ${isDarkMode ? "bg-primary-900/20 text-primary-400" : "bg-primary-50 text-primary-600"}`
                    : isFeatured
                        ? "bg-primary-500 border-primary-500 text-white hover:bg-primary-600 hover:border-primary-600 shadow-md disabled:opacity-60"
                        : isDarkMode
                            ? "bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600 disabled:opacity-60"
                            : "bg-slate-900 border-slate-900 text-white hover:bg-slate-700 disabled:opacity-60";

                return (
                    <div
                        key={plan.TierID}
                        className={`rounded-3xl border flex flex-col relative overflow-hidden ${isSingle ? "w-full max-w-sm" : ""} ${outerBorder}`}
                    >
                        {/* Top banner */}
                        <div className="px-6 pt-7 pb-5 bg-slate-900 relative">
                            {isCurrent ? (
                                <span className="absolute top-4 right-4 bg-primary-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow">✓ Current Plan</span>
                            ) : isFeatured ? (
                                <span className="absolute top-4 right-4 bg-primary-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow">Most Popular</span>
                            ) : null}
                            <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-1">Plan</p>
                            <h2 className="text-2xl font-bold text-white pr-24">{plan.TierName}</h2>
                        </div>

                        {/* Body */}
                        <div className="flex-1 flex flex-col p-6 gap-5">
                            <div className="text-center">
                                {promoTier && (
                                    <div className={`text-sm font-semibold line-through ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                                        ${Number(plan.Price).toFixed(2)}
                                    </div>
                                )}
                                <div className="flex items-baseline justify-center gap-1">
                                    <span className={`text-4xl font-extrabold ${isDarkMode ? "text-primary-400" : "text-primary-600"}`}>
                                        ${Number(promoTier ? promoTier.DiscountedPrice : plan.Price).toFixed(2)}
                                    </span>
                                    <span className={`text-sm font-semibold ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{billingSuffix(plan.BillingFrequency)}</span>
                                </div>
                                {/* The code lowers the payments made before it ends */}
                                {promoTier?.DiscountEndsOn && (
                                    <div className={`mt-1 text-xs font-semibold ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                        until {new Date(`${promoTier.DiscountEndsOn}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}, then ${Number(plan.Price).toFixed(2)}{billingSuffix(plan.BillingFrequency)}
                                    </div>
                                )}
                                {promoTier && (
                                    <span className="inline-block mt-1.5 text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-success/10 text-success">
                                        {promo.code} &middot; {promo.discountType === "free" ? "free" : promo.discountType === "percent" ? `${Number(promo.discountValue)}% off` : `$${Number(promo.discountValue || 0).toFixed(2)} off`}
                                    </span>
                                )}
                                {/* Reserve a fixed height so cards with and without a
                                    description keep their divider + feature list aligned. */}
                                <p className={`mt-2 text-sm min-h-[2.5rem] ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{plan.TierDescription || ""}</p>
                                {/* old {anyTrial && <p className="mt-1 text-xs font-semibold text-primary-600 min-h-[1rem]">{plan.TrialDays ? `${plan.TrialDays}-day free trial` : ""}</p>} */}
                                {/* Worded like the student plans page */}
                                {anyTrial && <p className="mt-1 text-xs font-semibold text-primary-600 min-h-[1rem]">{plan.TrialDays && !isCurrent ? `Free for ${plan.TrialDays} days, then billed ${plan.BillingFrequency === "yearly" ? "yearly" : "monthly"}` : ""}</p>}
                            </div>

                            <div className={`border-t ${isDarkMode ? "border-slate-700" : "border-slate-100"}`} />

                            <ul className="space-y-2 flex-1">
                                {features.map((f, i) => (
                                    <li key={i} className={`flex items-center gap-2 text-sm ${f.included ? (isDarkMode ? "text-slate-200" : "text-slate-700") : (isDarkMode ? "text-slate-600" : "text-slate-400")}`}>
                                        <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${f.included ? "bg-primary-100 text-primary-600" : "bg-slate-100 text-slate-400"}`}>
                                            {f.included ? "✓" : "✗"}
                                        </span>
                                        <span className={f.included ? "" : "line-through"}>{f.label}</span>
                                    </li>
                                ))}
                            </ul>

                            <button
                                //old onClick={() => !isCurrent && onChoose(plan)}
                                //old disabled={busy || isCurrent}
                                onClick={() => !isCurrent && !action?.disabled && onChoose(plan)}
                                disabled={busy || isCurrent || action?.disabled}
                                className={`w-full flex items-center justify-center gap-2 px-5 py-3 rounded-2xl font-bold border-2 transition-all mt-2 ${btnCls}`}
                            >
                                {busy ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />}
                                {/* old {isCurrent ? "Current Plan" : busy ? "Processing..." : promoFree ? "Claim" : "Subscribe"} */}
                                {isCurrent ? "Current Plan" : busy ? "Processing..." : promoFree ? "Claim" : action?.label || "Subscribe"}
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
